import React from 'react';
import { Phone, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Button } from './ui/button';

const Pricing = () => {
  const pricingTiers = [
    {
      name: "Débouchage",
      description: "Canalisation, évier, WC, douche",
      price: "179",
      unit: "€ HT",
      prefix: "À partir de",
      features: [
        "Diagnostic inclus",
        "Furet électrique",
        "Pompe à pression",
        "Intervention rapide"
      ],
      popular: true
    },
    {
      name: "Curage HP",
      description: "Hydrocurage haute pression",
      price: "289",
      unit: "€ HT",
      prefix: "Sur devis",
      features: [
        "Jusqu'à 300 bars",
        "100% écologique",
        "Détartrage complet",
        "Prix au mètre linéaire"
      ],
      popular: false
    },
    {
      name: "Inspection Caméra",
      description: "Diagnostic vidéo HD",
      price: "249",
      unit: "€ HT",
      prefix: "À partir de",
      features: [
        "Caméra endoscopique",
        "Localisation précise",
        "Sur devis",
        "Rapport détaillé en option"
      ],
      popular: false
    },
    {
      name: "Bac à Graisse",
      description: "Entretien & vidange",
      price: "289",
      unit: "€ HT",
      prefix: "À partir de",
      features: [
        "Pompage des graisses",
        "Nettoyage HP",
        "Remise en service",
        "Contrat disponible"
      ],
      popular: false
    },
    {
      name: "Pompe de Relevage",
      description: "Contrat d'entretien",
      price: "279",
      unit: "€ HT",
      prefix: "À partir de",
      features: [
        "Entretien régulier",
        "Diagnostic complet",
        "Intervention prioritaire",
        "Dépannage inclus"
      ],
      popular: false
    }
  ];

  return (
    <section id="tarifs" className="py-20 bg-gradient-to-br from-slate-900 via-sky-900 to-teal-900 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-20 w-64 h-64 bg-lime-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-sky-400/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-400/20 text-lime-300 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Tarifs Transparents
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Nos Tarifs de <span className="text-lime-400">Débouchage</span>
          </h2>
          <p className="text-sky-200 max-w-2xl mx-auto text-lg">
            Prix clairs et compétitifs, sans mauvaise surprise. Déplacement et devis gratuits.
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5 mb-12">
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className="relative rounded-2xl p-5 transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-sky-500 to-teal-500 shadow-xl hover:shadow-2xl"
            >
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold mb-1 text-white">
                  {tier.name}
                </h3>
                <p className="text-xs text-sky-100">
                  {tier.description}
                </p>
              </div>

              <div className="text-center mb-4">
                <p className="text-xs text-sky-100">
                  {tier.prefix}
                </p>
                <div className="flex items-baseline justify-center">
                  <span className="text-3xl font-bold text-lime-300">
                    {tier.price}
                  </span>
                  <span className="text-sm ml-1 text-sky-100">
                    {tier.unit}
                  </span>
                </div>
              </div>

              <ul className="space-y-2 mb-5">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <CheckCircle 
                      size={14} 
                      className="text-lime-300" 
                    />
                    <span className="text-xs text-sky-50">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="block">
                <Button 
                  className="w-full py-2 rounded-xl font-semibold text-sm transition-all duration-300 bg-white hover:bg-sky-50 text-sky-700"
                >
                  <Phone size={14} className="mr-1" />
                  Appeler
                </Button>
              </a>
            </div>
          ))}
        </div>

        {/* Payment methods */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-white font-semibold mb-2">Modes de paiement acceptés</p>
              <div className="flex items-center gap-4 text-sky-200 text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-lime-400 rounded-full" />
                  Carte Bancaire
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-lime-400 rounded-full" />
                  Espèces
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-lime-400 rounded-full" />
                  Chèque
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-lime-400 rounded-full" />
                  Virement
                </span>
              </div>
            </div>
            <div className="h-12 w-px bg-white/20 hidden md:block" />
            <div className="text-center">
              <p className="text-lime-400 font-bold text-lg">Paiement en 3x sans frais</p>
              <p className="text-sky-200 text-sm">Pour les interventions > 300€</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
